import React, { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { preloadPublicHomepageStats, readHomepageStatsCache } from '../services/siteContentService';
import './HomepageStatsSection.css';

const SvgIcon = ({ children }) => (
  <svg viewBox="0 0 48 48" fill="none" stroke="currentColor" strokeWidth="2.1" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
    {children}
  </svg>
);

const ICONS = {
  well: (
    <SvgIcon>
      <ellipse cx="24" cy="38" rx="15" ry="5" />
      <path d="M9 38v3c0 3.2 6.7 5.5 15 5.5s15-2.3 15-5.5v-3" />
      <ellipse cx="24" cy="38" rx="8" ry="2.6" />
      <path d="M16 38V16M32 38V16" />
      <path d="M12 16h24" />
      <path d="M12 16L24 7l12 9" />
      <path d="M24 16v10" />
      <path d="M20.5 26h7v6h-7z" />
      <ellipse cx="24" cy="26" rx="3.5" ry="1.2" />
    </SvgIcon>
  ),
  basket: (
    <SvgIcon>
      <path d="M12 20h24l-2 16H14L12 20z" />
      <path d="M16 20c0-6 3.5-10 8-10s8 4 8 10" />
      <path d="M18 26h12M18 31h12" />
    </SvgIcon>
  ),
  plate: (
    <SvgIcon>
      <ellipse cx="24" cy="34" rx="14" ry="4" />
      <path d="M12 28c0-10 5.5-16 12-16s12 6 12 16" />
      <path d="M24 12v-3" />
    </SvgIcon>
  ),
  child: (
    <SvgIcon>
      <circle cx="24" cy="14" r="8" />
      <path d="M8 42c1.6-13 8.2-18 16-18s14.4 5 16 18" />
    </SvgIcon>
  ),
  ram: (
    <SvgIcon>
      <path d="M16 19c-7-9 2-14 9-6" />
      <path d="M18 18c-4-7 3-10 6-3.5" />
      <ellipse cx="14" cy="26" rx="6.2" ry="5.2" />
      <ellipse cx="30" cy="28" rx="12.5" ry="8.5" />
      <path d="M22 35.5v9M28 36.5v8M34 36.5v8M39 35.5v9" />
      <path d="M42 23c4.2 1 4.2 8.2-1.2 7" />
      <path d="M16 22l-4.5-5" />
      <circle cx="11.4" cy="25.2" r="1.2" fill="currentColor" stroke="none" />
    </SvgIcon>
  ),
  truck: (
    <SvgIcon>
      <rect x="2" y="14" width="28" height="18" rx="1.6" />
      <path d="M30 20h8l5 8v4H30z" />
      <path d="M32 22h5.2l2.6 5.2H32z" />
      <circle cx="11" cy="35" r="4" />
      <circle cx="35" cy="35" r="4" />
      <circle cx="11" cy="35" r="1.5" />
      <circle cx="35" cy="35" r="1.5" />
    </SvgIcon>
  ),
  shirt: (
    <SvgIcon>
      <path d="M16 12l8 6 8-6 6 4-5 4v18H15V20l-5-4 6-4z" />
    </SvgIcon>
  ),
};

const iconForTitle = (title) => {
  const t = String(title || '');
  if (t.includes('سقيا')) return ICONS.well;
  if (t.includes('قفة')) return ICONS.basket;
  if (t.includes('مائدة')) return ICONS.plate;
  if (t.includes('أيتام')) return ICONS.child;
  if (t.includes('أضحية') || t.includes('اضحية')) return ICONS.ram;
  if (t.includes('قوافل')) return ICONS.truck;
  if (t.includes('كسوة')) return ICONS.shirt;
  return null;
};

const digitsOf = (value) => {
  const digits = String(value ?? '').replace(/[^\d]/g, '');
  return digits ? Number(digits) : null;
};

const StatValue = ({ value }) => {
  const end = digitsOf(value);
  const [count, setCount] = useState(0);
  const [started, setStarted] = useState(false);
  const ref = useRef(null);

  useEffect(() => {
    if (end === null) return undefined;
    const node = ref.current;
    if (!node) return undefined;

    const start = () => setStarted(true);
    if (typeof IntersectionObserver === 'undefined') {
      start();
      return undefined;
    }

    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          start();
          observer.disconnect();
        }
      },
      { threshold: 0.15, rootMargin: '80px' }
    );
    observer.observe(node);
    return () => observer.disconnect();
  }, [end]);

  useEffect(() => {
    if (!started || end === null) return undefined;
    if (end === 0) {
      setCount(0);
      return undefined;
    }

    let current = 0;
    const step = Math.max(1, Math.ceil(end / 60));
    const timer = setInterval(() => {
      current += step;
      if (current >= end) {
        setCount(end);
        clearInterval(timer);
      } else {
        setCount(current);
      }
    }, 24);
    return () => clearInterval(timer);
  }, [started, end]);

  if (end === null) {
    return <p className="text-3xl font-bold text-gray-900">{value}</p>;
  }

  return (
    <p ref={ref} className="text-3xl font-bold text-gray-900">
      {started ? String(count) : '0'}
    </p>
  );
};

const HomepageStatsSection = () => {
  const { t } = useTranslation();
  const [stats, setStats] = useState(() => readHomepageStatsCache() || []);

  useEffect(() => {
    preloadPublicHomepageStats('home')
      .then((data) => {
        if (Array.isArray(data) && data.length) setStats(data);
      })
      .catch(() => {});
  }, []);

  return (
    <section className="py-16 bg-white">
      <div className="container mx-auto px-4">
        <h2 className="text-3xl md:text-4xl font-bold text-center text-gray-900 mb-4">
          {t('public.home.statsTitle')}
        </h2>
        <div className="w-24 h-1 bg-brand-divider mx-auto mb-12" />

        <div className="grid grid-cols-2 md:grid-cols-4 gap-6">
          {stats.map((stat) => {
            const icon = iconForTitle(stat.title);
            return (
            <div
              key={stat.id}
              className="bg-gray-50 rounded-xl p-6 text-center border border-gray-100 hover:shadow-md transition-shadow homepage-stat-card"
            >
              <h3 className="text-lg font-semibold text-brand-primary mb-2">{stat.title}</h3>
              <div className="homepage-stat-value-row">
                <StatValue value={stat.value} />
                {icon && <span className="homepage-stat-icon">{icon}</span>}
              </div>
              {stat.subtitle && (
                <p className="text-sm text-gray-500 mt-1">{stat.subtitle}</p>
              )}
            </div>
            );
          })}
        </div>
      </div>
    </section>
  );
};

export default HomepageStatsSection;
